import { withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import { withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { formatHeaderValue } from "@components/readOnlyList/ReadOnlyList";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import {
    enhanceHeaderDataWithSmartFilter,
    getDataForReadOnlyListSmartFilter,
    prepareReadOnlyListSmartFilterProps
} from "@components/smart/smartValueHelper/ValueHelper.utils";
import { InvoiceIssuedEntity, InvoiceReceivedEntity } from "@odata/GeneratedEntityTypes";
import { BankTransactionTypeCode, DocumentTypeCode } from "@odata/GeneratedEnums";
import { isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import i18next from "i18next";

import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { ValueType } from "../../../enums";
import { IAfterSaveEventArgs } from "../../../model/Model";
import DateType from "../../../types/Date";
import {
    createDefaultPairFilter,
    getCashboxPrefix,
    getCashTransactionType,
    IPairTableViewBaseProps
} from "../../banks/bankTransactions/BankTransactions.utils";
import { PairTableViewForExtend } from "../../banks/PairTableView";


class CashReceiptsPairTableView extends PairTableViewForExtend {
    constructor(props: IPairTableViewBaseProps) {
        super(props);
        this.presetDefaultFilters = this.presetDefaultFilters.bind(this);

        this.presetDefaultFilters();
    }

    getTitle = () => {
        return this.payment === BankTransactionTypeCode.IncomingPayment ? i18next.t("Cash:PairDialog.TitleReceived") : i18next.t("Cash:PairDialog.Title");
    };

    get payment() {
        const type = this.getRootStorage().getCustomData().type;
        return type === DocumentTypeCode.CashReceiptReceived ? BankTransactionTypeCode.IncomingPayment : BankTransactionTypeCode.OutgoingPayment;
    }

    get transactionAmountProp() {
        return this.payment === BankTransactionTypeCode.OutgoingPayment
                ? InvoiceReceivedEntity.TransactionAmountToPay
                : InvoiceIssuedEntity.TransactionAmountToReceive;
    }

    getTransactionType = () => {
        return getCashTransactionType(this.props.rootStorage);
    };

    get successSubtitle(): string {
        return i18next.t("Cash:PairDialog.Paired");
    }

    presetDefaultFilters() {
        createDefaultPairFilter(this.props.storage, this.payment);
        this.prepareReadOnlyListSmartFilterProps();
        this.props.storage.applyFilters();
    }

    prepareReadOnlyListSmartFilterProps() {
        const { entity } = this.props.rootStorage.data;
        const prop = this.transactionAmountProp;
        const amount = Math.abs(entity.TransactionAmount);
        prepareReadOnlyListSmartFilterProps(this.props.storage, [{
            id: prop,
            value: amount,
            filterValue: JSON.stringify({ [prop]: amount, TransactionCurrencyCode: entity.TransactionCurrency?.Code }),
            valueType: ValueType.Number
        }]);
    }

    handleFormAfterSave(args: IAfterSaveEventArgs) {
        super.handleFormAfterSave(args);

        getDataForReadOnlyListSmartFilter.reset();
        this.prepareReadOnlyListSmartFilterProps();
    }

    getHeaderData = (): IReadOnlyListItem[] => {
        const { rootStorage, storage } = this.props;
        const cashBoxPrefix = getCashboxPrefix(rootStorage.data.entity);
        const cashBox = rootStorage.data.entity.CashBox?.BalanceSheetAccountNumberSuffix;
        const name = rootStorage.data.entity.CashBox?.Name;

        const isCba = isCashBasisAccountingCompany(rootStorage.context);

        const cashBoxItem = isCba ? {
            label: rootStorage.t("Cash:PairDialog.CashBox"),
            value: formatHeaderValue(name),
        } : {
            label: rootStorage.t("Cash:PairDialog.CashBox"),
            value: formatHeaderValue(cashBox && `${cashBoxPrefix}${(cashBox)}`),
            description: name ? ` | ${name}` : ""
        };

        return enhanceHeaderDataWithSmartFilter(storage, [{
            label: rootStorage.t("Cash:PairDialog.Date"),
            value: formatHeaderValue(DateType.format(rootStorage.data.entity.DateIssued))
        }, {
            id: this.transactionAmountProp,
            label: rootStorage.t("Cash:PairDialog.Amount"),
            value: formatHeaderValue(this.formatCurrency(rootStorage.data.entity.TransactionAmount))
        }, {
            ...cashBoxItem
        }, {
            label: rootStorage.t("Cash:PairDialog.Note"),
            value: formatHeaderValue(rootStorage.data.entity.Purpose)
        }]);
    };
}

export default withBusyIndicator({ passBusyIndicator: true, isDelayed: true })(withPermissionContext(withConfirmationDialog(CashReceiptsPairTableView)));